import startOfToday from 'date-fns/startOfToday';
import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import { LocationEnum } from '@/__generated__/graphql';
import {
  makeGuestsValid,
  makeRoomsValid,
  makeRatingValid,
  getDefaultGuests,
  getDefaultRooms,
} from './guests';

export const getInitialSearchState = (params) => {
  const date = parseDate(params);
  const inexactStay = parseInexactStay(params);
  const pets = parsePets(params);
  const guests = makeGuestsValid(params);
  const rooms = makeRoomsValid(params);
  const rating = makeRatingValid(params?.rating);
  const location = parseLocation(params);
  const tab = params?.tab;
  const price = parsePrice(params);
  const amenities = parseAmenities(params);
  return {
    date,
    inexactStay,
    pets,
    guests,
    rooms,
    rating,
    location,
    tab,
    price,
    amenities,
  };
};

function parsePrice(params) {
  const defaultValue = { min: null, max: null };
  if (!params) return defaultValue;
  return { min: params.priceMin || null, max: params.priceMax || null };
}

export function parseAmenities(params) {
  if (!params?.amenities?.length) return [];
  return (params.amenities || '').split(',').map(Number).filter(Boolean);
}

function parseDate(params) {
  const defaultValue = { startDate: null, endDate: null };
  if (
    !params ||
    typeof params.startDate !== 'string' ||
    typeof params.endDate !== 'string'
  ) {
    return defaultValue;
  }
  const startDate = new Date(params.startDate);
  const endDate = new Date(params.endDate);
  const isStartInvalid = String(startDate).toLowerCase() === 'invalid date';
  const isEndInvalid = String(endDate).toLowerCase() === 'invalid date';
  if (isStartInvalid || isEndInvalid) {
    return defaultValue;
  }
  return { startDate: params.startDate, endDate: params.endDate };
}

function parseInexactStay(params) {
  const defaultValue = 0;
  if (!params) {
    return defaultValue;
  }
  if (!['0', '1', '2', '3', '7'].includes(`${params.inexactStay}`)) {
    return defaultValue;
  }
  return Number(params.inexactStay);
}

function parsePets(params) {
  if (!params || !params.pets) return false;
  return params.pets === 'enabled';
}

function parseLocation(params) {
  const defaultValue = { name: '', type: '', id: '' };
  if (
    !params ||
    !(params.locationName || params.locationType || params.locationId)
  ) {
    return defaultValue;
  }
  const { locationName, locationType, locationId } = params;
  if (
    !locationId &&
    (typeof locationName !== 'string' || typeof locationType !== 'string')
  ) {
    return defaultValue;
  }
  return { name: locationName, type: locationType, id: locationId };
}

/**
 * Constructs a search query string from the given parameters.
 * @param {Object} searchParams - The search parameters.
 * @param {Object} searchParams.guests - Total number of guests, with properties like adults, children, etc.
 * @param {Object} searchParams.date - The check-in and check-out dates.
 * @param {string} searchParams.date.startDate - The start date of the stay.
 * @param {string} searchParams.date.endDate - The end date of the stay.
 * @param {Object} searchParams.location - The desired location of the stay.
 * @param {string} searchParams.location.id - The id of the location.
 * @param {boolean} searchParams.pets - Indicates if the search includes pets.
 * @param {number} searchParams.inexactStay - Number of expanded search dates.
 * @param {Object} searchParams.price - Price of expanded search.
 * @param {Object} searchParams.amenities - amenities of expanded search.
 * @returns {string} The constructed search query string, starting with '?' if not empty.
 */
export const buildSearchQuery = ({
  guests,
  date,
  location,
  pets,
  inexactStay,
  price,
  amenities,
  rooms,
  rating,
}) => {
  const params = {
    startDate: date?.startDate
      ? format(new Date(date.startDate), 'yyyy-MM-dd')
      : undefined,
    endDate: date?.endDate
      ? format(new Date(date.endDate), 'yyyy-MM-dd')
      : undefined,
    inexactStay: inexactStay && inexactStay.toString(),
    pets: pets ? 'enabled' : false,
    locationId: location?.id,
    ...guests,
    ...rooms,
    rating: rating > 0 || rating === 0 ? `${rating}` : undefined,
    priceMin: price?.min ? price.min : undefined,
    priceMax: price?.max ? price.max : undefined,
    amenities: amenities?.length ? amenities : undefined,
  };

  return joinSearchQuery(params);
};

export const joinSearchQuery = (params) => {
  const esc = encodeURIComponent;

  const url = Object.keys(params)
    .filter((key) => !!params[key])
    .map((k) => esc(k) + '=' + esc(params[k]))
    .join('&');

  return url.length ? `?${url}` : '';
};

/**
 * @typedef {Object} AvailabilityDateRange
 * @property {Date} startDate - The start date of the availability period.
 * @property {Date} endDate - The end date of the availability period.
 * @property {number} dateFlexibility - The number of days of flexibility in the stay duration.
 */

/**
 * Prepares the availability date range and flexibility.
 *
 * @param {Object} date - An object containing the startDate and endDate properties.
 * @param {number} [inexactStay] - An optional number indicating the flexibility of the stay duration.
 * @returns {AvailabilityDateRange} An object containing the startDate, endDate, and dateFlexibility.
 */
function prepareAvailability(date = {}, inexactStay) {
  let startDate = undefined;
  let endDate = undefined;
  let dateFlexibility = undefined;

  if (
    !date.startDate ||
    !date.endDate ||
    new Date(date.endDate) < new Date(date.startDate)
  ) {
    return { startDate, endDate, dateFlexibility };
  }

  const currentDate = startOfToday();
  const isStartDateOld = new Date(date.startDate) < currentDate;
  const isEndDateOld = new Date(date.endDate) < currentDate;
  if (isStartDateOld || isEndDateOld) {
    startDate = addDays(new Date(currentDate), 1);
    endDate = addDays(new Date(currentDate), 7);
    dateFlexibility = 3;
    return { startDate, endDate, dateFlexibility };
  }

  startDate = new Date(date.startDate);
  endDate = new Date(date.endDate);
  dateFlexibility = typeof inexactStay === 'number' ? inexactStay : undefined;
  return { startDate, endDate, dateFlexibility };
}

/**
 * @typedef {Object} GuestsObj
 * @property {number} adults - Number of adults.
 * @property {number} children - Number of children.
 * @property {number} infants - Number of infants.
 */
/**
 * @typedef {Object} OccupancyObj
 * @property {GuestsObj} guests - Guests.
 * @property {boolean} pets - Pets.
 */

/**
 * Prepares the guests.
 *
 * @param {Object} guests - An object containing guests.
 * @param {boolean} pets - True if guests will bring pets.
 * @returns {OccupancyObj} An object containing guests and pets.
 */
function prepareOccupancy(guests = {}, pets = false) {
  const totalGuests = Object.keys(guests).reduce((acc, type) => {
    const qty = guests[type] ? guests[type] : 0;
    return acc + qty;
  }, 0);
  if (totalGuests <= 0 || guests.adults <= 0) {
    return { guests: getDefaultGuests(), pets: false };
  }
  return { guests: makeGuestsValid(guests), pets };
}

/**
 * @typedef {Object} LocationObj
 * @property {string} name - Name of location.
 * @property {string} type - Type of location, i.e. Country.
 */

/**
 * Prepares the location.
 *
 * @param {Object} location - An object containing locations' name and type.
 * @returns {LocationObj} An object containing locations' name and type.
 */
function prepareLocation(location = { name: '', type: '', id: '' }) {
  if (!location?.id && (!location?.name || !location?.type)) {
    return { name: 'Sweden', type: LocationEnum.Country, id: 'SE' };
  }
  return location;
}

/**
 * Prepares the price.
 *
 * @param {Object} price - An object containing price' min and max.
 * @returns {LocationObj} An object containing price.
 */
function preparePrice(price = { min: null, max: null }) {
  return price;
}

/**
 * Prepares the amenities.
 *
 * @param {Object} amenities - An object containing amenities id.
 * @returns {LocationObj} An object containing amenities.
 */
function prepareAmenities(amenities = []) {
  if (!amenities?.length) {
    return undefined;
  }
  return amenities;
}

/**
 * Prepares the rooms.
 *
 * @param {Object} rooms - An object containing rooms id.
 * @returns {LocationObj} An object containing rooms.
 */
function prepareRooms(rooms = {}) {
  const totalRooms = Object.keys(rooms).reduce((acc, type) => {
    const qty = rooms[type] ? rooms[type] : 0;
    return acc + qty;
  }, 0);

  if (totalRooms <= 0) {
    return getDefaultRooms();
  }
  return makeRoomsValid(rooms);
}

/**
 * Prepares the rating.
 *
 * @param {Object} rooms - An object containing rating id.
 * @returns {LocationObj} An object containing rating min max.
 */
function prepareRating(rating = null) {
  if (rating > 0 || rating === 0) {
    return makeRatingValid(rating);
  }
  return null;
}

export function prepareBookingParams(params = {}) {
  const { dateFlexibility, ...dateRange } = prepareAvailability(
    params.date,
    params.inexactStay
  );
  const price = preparePrice(params.price);
  const location = prepareLocation(params.location);
  const amenities = prepareAmenities(params.amenities);
  const rooms = prepareRooms(params.rooms);
  const rating = prepareRating(params.rating);
  const { guests, pets } = prepareOccupancy(params.guests, params.pets);
  return {
    dateFlexibility,
    dateRange,
    location,
    guests,
    pets,
    price,
    amenities,
    rooms,
    rating,
    tab: params.tab,
  };
}
